import { computed, ref, Ref } from 'vue';
import useModal, { modals } from '../../../composables/useModal';
import { Type, useRouteQueryRef } from '../../../composables/useRouteQuery';

const availableFilters: Ref<Record<string, any>> = ref({});
const useMainTableFilters = () => {
  const filters = useRouteQueryRef().ref('filters', Type.isJson<Record<string, any>>, undefined);
  const { open: openFiltersModal } = useModal(modals.MAIN_TABLE_FILTERS);

  const setFilters = (newFilters: Record<string, any> | undefined) => {
    filters.value = newFilters;
  };

  const setAvailableFilters = (newAvailableFilters: Record<string, any>) => {
    availableFilters.value = newAvailableFilters;
  };

  // if there is nothing in the query, set default values for available filters
  const getDefaultFilters = (): Record<string, any> | undefined => {
    const defaultFilters: Record<string, any> = {};
    for (const key in availableFilters.value) {
      const filter = availableFilters.value[key];
      if (filter.default !== undefined) {
        defaultFilters[key] = filter.default;
      }
    }
    if (Object.keys(defaultFilters).length === 0) return undefined;

    return defaultFilters;
  };

  const cleanedUpfilterValues = () => {
    const cleanedFilters: Record<string, any> = {};
    for (const key in filters.value) {
      const filter = availableFilters.value[key];

      if (!filter) continue;

      if (filter.type === 'enum' && filter.options) {
        const value = filters.value[key];
        if (Array.isArray(value)) {
          const validValues = value.filter((v) => filter.options!.includes(v));
          if (validValues.length > 0) {
            cleanedFilters[key] = validValues;
          }
        } else if (filter.options.includes(value)) {
          cleanedFilters[key] = value;
        }
      } else if (filter.type === 'boolean') {
        const value = filters.value[key];
        if (typeof value === 'boolean') {
          cleanedFilters[key] = value;
        }
      } else if (filter.type === 'string') {
        const value = filters.value[key];
        if (typeof value === 'string' && value.trim() !== '') {
          cleanedFilters[key] = value;
        }
      }
    }

    if (Object.keys(cleanedFilters).length === 0) {
      return undefined;
    }

    return cleanedFilters;
  };

  const cleanUpOrSetDefaultFilters = () => {
    const cleanedFilters = cleanedUpfilterValues();

    if (cleanedFilters) {
      setFilters(cleanedFilters);
      return;
    }

    const defaultFilters = getDefaultFilters();
    if (defaultFilters) {
      setFilters(defaultFilters);
      return;
    }

    setFilters(undefined);
  };

  return {
    filters,
    availableFilters,
    showFiltersButton: computed(() => Object.keys(availableFilters.value).length > 0),
    activeFiltersCount: computed(() => (filters.value ? Object.keys(filters.value).length : 0)),
    openFiltersModal,
    setFilters,
    clearFilters: () => setFilters(undefined),
    setAvailableFilters,
    cleanUpOrSetDefaultFilters,
  };
};

export default useMainTableFilters;
